import type { Mode } from '../api/types';

/**
 * 軸の表示。主軸 4 本＋副軸 2 本（doc/THEMES.md「6 本目の軸」）。
 *
 * 「16タイプ性格診断」「32タイプ性格診断」を名称に使わない、他社のタイプ名も使わない
 * （doc/assessment.md §0）。副軸は自前の日本語で「主張型／受容型」「泰然型／繊細型」と呼ぶ。
 */
export interface AxisLabel {
  readonly axis: string;
  /** スコアが高いほうの極。 */
  readonly high: { readonly code: string; readonly name: string };
  readonly low: { readonly code: string; readonly name: string };
  readonly caption: string;
}

export const AXIS_LABELS: readonly AxisLabel[] = [
  { axis: 'EI', high: { code: 'E', name: '外向' }, low: { code: 'I', name: '内向' }, caption: 'エネルギーの向き先' },
  { axis: 'SN', high: { code: 'N', name: '直観' }, low: { code: 'S', name: '感覚' }, caption: '情報の受け取り方' },
  { axis: 'TF', high: { code: 'F', name: '感情' }, low: { code: 'T', name: '思考' }, caption: '判断の基準' },
  { axis: 'JP', high: { code: 'J', name: '判断' }, low: { code: 'P', name: '知覚' }, caption: '外界への構え' },
  { axis: 'AY', high: { code: 'A', name: '主張型' }, low: { code: 'Y', name: '受容型' }, caption: '自分をどれだけ押し出すか' },
  { axis: 'CV', high: { code: 'C', name: '泰然型' }, low: { code: 'V', name: '繊細型' }, caption: '情動の波の穏やかさ' },
];

export const FACET_LABELS: Record<string, string> = {
  'EI.social': '社交性',
  'EI.energy': '回復のしかた',
  'EI.expressive': '表出性',
  'SN.focus': '情報の焦点',
  'SN.imagination': '想像',
  'SN.novelty': '新規志向',
  'TF.criteria': '判断基準',
  'TF.empathy': '共感',
  'TF.conflict': '対立の扱い',
  'JP.planning': '計画性',
  'JP.closure': '決着志向',
  'JP.order': '秩序',
  'AY.confidence': '自己確信',
  'AY.assertion': '主張',
  'AY.initiative': '主導',
  'CV.stress': 'ストレス反応',
  'CV.perfection': '完璧志向',
  'CV.mood': '気分の安定',
  // 結果に保存する短いキー（packages/master の contract.ts。results.facetProfile）。
  'EI.expr': '表出性',
  'SN.imag': '想像',
  'SN.novel': '新規志向',
  'TF.crit': '判断基準',
  'TF.emp': '共感',
  'TF.conf': '対立の扱い',
  'JP.plan': '計画性',
  'JP.clos': '決着志向',
  'AY.conf': '自己確信',
  'AY.assert': '主張',
  'AY.init': '主導',
  'CV.perf': '完璧志向',
  // v1 のキー（古い結果）
  'AT.confidence': '自己確信',
  'AT.stress': 'ストレス反応',
  'AT.perfection': '完璧志向',
  'AT.conf': '自己確信',
  'AT.perf': '完璧志向',
};

export interface ModeInfo {
  readonly mode: Mode;
  readonly label: string;
  readonly count: number;
  readonly minutes: string;
  readonly summary: string;
  /** depth = 同じ設問の深さ違い（quick ⊂ standard ⊂ deep）。instrument = 別の質問紙（ビッグファイブ）。 */
  readonly kind: 'depth' | 'instrument';
}

/**
 * トップで選ばせるのは「診断の深さ」であって、タイプの数ではない（doc/assessment.md §3.4）。
 * 既定は deep（全 18 側面・72 問）。短い版は測る側面が少なく、見立ての精度が落ちることを添える（doc/ASSESSMENT.md §2）。
 * ビッグファイブ版は深さではなく別の質問紙（60 問）。5 因子が主結果で、キャラと型は 5 因子から写した目安（doc/ASSESSMENT.md §9）。
 */
export const DEFAULT_MODE: Mode = 'deep';
export const MODE_INFO: readonly ModeInfo[] = [
  { mode: 'deep', label: 'じっくり', count: 72, minutes: '約10分', summary: '18 の側面すべてを測ります。主張型か受容型か、泰然型か繊細型かまで分かり、見立てがいちばん確かです。', kind: 'depth' },
  { mode: 'standard', label: 'ふつう', count: 48, minutes: '約6分', summary: '主軸 4 本・12 の側面。副軸（主張・情動）は測りません。', kind: 'depth' },
  { mode: 'quick', label: 'さっと', count: 24, minutes: '約3分', summary: 'まず結果が見たいときに。8 の側面だけなので、見立ては粗くなります。', kind: 'depth' },
  {
    mode: 'bigfive', label: 'ビッグファイブ', count: 60, minutes: '約8分', kind: 'instrument',
    summary: '性格心理学で最も裏付けの多い 5 因子（外向性・協調性・誠実性・神経症傾向・開放性）と 15 の側面を測ります。キャラと型は 5 因子から写した目安です。',
  },
];
export const DEPTH_MODE_INFO: readonly ModeInfo[] = MODE_INFO.filter((m) => m.kind === 'depth');
export const INSTRUMENT_MODE_INFO: readonly ModeInfo[] = MODE_INFO.filter((m) => m.kind === 'instrument');

export const isMode = (value: string | null | undefined): value is Mode => MODE_INFO.some((m) => m.mode === value);

/**
 * ビッグファイブのスコアの水準。基準が無い間は素点（50 が「どちらともいえない」）なので目盛りの中央から見る。
 * 基準ができたら百分位（人の中での位置）で、±1 SD（16 / 84）とその半分（31 / 69）で切る。
 */
export type BigFiveLevel = '低い' | 'やや低い' | '中程度' | 'やや高い' | '高い';

/** 5 因子の名前。説明文や側面の正は bigfive.json（`fetchBigFiveSet`）で、ここは JSON が届く前の見出しと前回比較にだけ使う。 */
export const BIG_FIVE_DOMAIN_NAMES: Record<string, string> = { E: '外向性', A: '協調性', C: '誠実性', N: '神経症傾向', O: '開放性' };
export const BIG_FIVE_DOMAIN_ORDER = ['E', 'A', 'C', 'N', 'O'] as const;

export function bigFiveLevel(score: number, normed: boolean): BigFiveLevel {
  const cuts = normed ? [16, 31, 69, 84] : [31, 43, 58, 70];
  if (score < cuts[0]) return '低い';
  if (score < cuts[1]) return 'やや低い';
  if (score < cuts[2]) return '中程度';
  if (score < cuts[3]) return 'やや高い';
  return '高い';
}

/**
 * テーマの人数に合う深さ（doc/ASSESSMENT.md §6.4）。16 人以下のテーマは主軸だけでキャラが決まるので、
 * 副軸の 24 問を答えてもらっても見立ては変わらない。32 人以上は副軸が効くので deep。
 */
export function recommendedMode(typeCount: number | undefined): Mode {
  return (typeCount ?? 64) >= 32 ? 'deep' : 'standard';
}

export const SCALE_HINT = ['まったく違う', 'どちらかといえば違う', 'どちらともいえない', 'どちらかといえばそう', 'まさにそう'];

/**
 * ユングの類型（『心理学的類型』1921）。主軸 4 文字から、主機能と補助機能を読む。
 *
 * ユングは態度（外向／内向）× 機能（思考・感情・感覚・直観）で 8 つの型を立てた。
 * 4 文字の型では、J は判断機能（T/F）を、P は知覚機能（S/N）を外へ向けることを表すので、
 *   外向 + J → 判断機能が主（外向的思考／感情）、外向 + P → 知覚機能が主（外向的感覚／直観）、
 *   内向 + J → 知覚機能が主（内向的感覚／直観）、内向 + P → 判断機能が主（内向的思考／感情）。
 * 補助機能はもう一方の機能で、態度は主機能と逆になる。
 * 4 文字そろっていないコード（8 人・4 人のテーマの INT・IN）には付けない。
 */
export interface JungianType {
  /** 主機能。「内向的直観」など。 */
  readonly dominant: string;
  /** 補助機能。「外向的思考」など。 */
  readonly auxiliary: string;
}

const FUNCTION_NAME: Record<string, string> = { S: '感覚', N: '直観', T: '思考', F: '感情' };

export function jungianType(code: string): JungianType | null {
  const letters = code.split('-')[0];
  if (!/^[EI][SN][TF][JP]$/.test(letters)) return null;
  const [attitude, perceiving, judging, orientation] = letters.split('');
  const extraverted = attitude === 'E';
  // 外へ向ける機能（J なら判断機能、P なら知覚機能）。外向型ならそれが主機能、内向型ならもう一方が主機能
  const outward = orientation === 'J' ? judging : perceiving;
  const inward = orientation === 'J' ? perceiving : judging;
  const dominantFn = extraverted ? outward : inward;
  const auxiliaryFn = extraverted ? inward : outward;
  const dominantAttitude = extraverted ? '外向的' : '内向的';
  const auxiliaryAttitude = extraverted ? '内向的' : '外向的';
  return {
    dominant: `${dominantAttitude}${FUNCTION_NAME[dominantFn]}`,
    auxiliary: `${auxiliaryAttitude}${FUNCTION_NAME[auxiliaryFn]}`,
  };
}
